
/* ===== Component: Layout – Header ===== */
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SolutionsDropdown from './SolutionsDropdown';

/**
 * @component Header
 * @description Main navigation header with scroll behavior and refined dropdowns
 * @returns {JSX.Element} Header with responsive navigation and premium styling
 */
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /**
   * Toggles the mobile menu open/closed state
   */
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  /**
   * Effect to scroll to top when route changes
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    // Close mobile menu on route change
    setIsMenuOpen(false);
  }, [location.pathname]);
  
  /**
   * Effect to track scroll position and add background to header
   */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Effect to handle body scroll lock when mobile menu is open
   */
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = ''; // Cleanup on component unmount
    };
  }, [isMenuOpen]);


  return (
    <> {/* Use a Fragment to allow mobile menu to be outside the header stacking context */}
      <header 
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          scrolled 
            ? "bg-white/95 backdrop-blur-sm shadow-sm border-b border-forti-fi-lavender/30" // Added a subtle border color
            : "bg-white border-b border-transparent" // Transparent border initially to prevent layout shift
        }`}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <div>
            <Link to="/" className="flex items-center">
              <img 
                alt="Forti-Fi" 
                onError={e => {
                  const img = e.target as HTMLImageElement;
                  img.alt = "Forti-Fi";
                  img.src = "";
                  img.style.display = "none";
                  // Create and insert text fallback
                  const textFallback = document.createElement('span');
                  textFallback.textContent = "Forti-Fi";
                  textFallback.className = "font-serif text-forti-fi text-xl font-bold";
                  img.parentNode?.appendChild(textFallback);
                }} 
                src="/images/forti-fi-logo-full.png" 
                className="h-12 md:h-40 w-auto object-contain" // Increased from h-10 to h-12
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <Link 
              to="/" 
              className={`px-3 py-2 lg:px-4 transition-colors hover:text-forti-fi-DEFAULT highlight-underline ${
                location.pathname === '/' ? 'text-forti-fi-DEFAULT font-medium' : 'text-forti-fi-charcoal'
              }`}
            >
              Home
            </Link>
            <SolutionsDropdown />
            <Link 
              to="/about" 
              className={`px-3 py-2 lg:px-4 transition-colors hover:text-forti-fi-DEFAULT highlight-underline ${
                location.pathname === '/about' ? 'text-forti-fi-DEFAULT font-medium' : 'text-forti-fi-charcoal'
              }`}
            >
              About
            </Link>
            <Button 
              asChild 
              variant="gradient" 
              className="text-white ml-2 px-4 py-2 lg:px-6" 
            >
              <Link to="/contact">
                Contact
              </Link>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMenu} aria-label="Toggle Menu" className="text-forti-fi-DEFAULT">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - Updated for better centering and visibility */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 z-[9999] bg-forti-fi-charcoal/95 backdrop-blur-md md:hidden" 
          onClick={() => setIsMenuOpen(false)} 
        >
          <div 
            className="fixed top-0 left-1/2 transform -translate-x-1/2 h-full w-4/5 max-w-sm bg-forti-fi-charcoal shadow-xl p-6 pt-20 flex flex-col space-y-3 overflow-y-auto" 
            onClick={e => e.stopPropagation()} 
          >
            {/* Improved close button position - always visible at the top right */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsMenuOpen(false)} 
              aria-label="Close Menu" 
              className="absolute top-4 right-4 text-white bg-forti-fi-dark/50 hover:bg-forti-fi-dark z-50"
            >
              <X className="h-5 w-5" />
            </Button>
            
            <div className="flex justify-center mb-6">
              <img 
                src="/images/forti-fi-logo-full-white.png" 
                alt="Forti-Fi Icon" 
                className="h-10 w-auto" 
                onError={e => {
                  const img = e.target as HTMLImageElement;
                  img.alt = "Forti-Fi";
                  img.src = "";
                  img.style.display = "none";
                  // Create and insert text fallback
                  const textFallback = document.createElement('span');
                  textFallback.textContent = "Forti-Fi";
                  textFallback.className = "font-serif text-white text-xl font-bold";
                  img.parentNode?.appendChild(textFallback);
                }} 
              />
            </div>
            <div className="flex flex-col items-center text-center w-full">
              <Link 
                to="/" 
                className="block py-2.5 px-3 w-full text-lg text-white hover:text-forti-fi transition-colors rounded-md hover:bg-white/5 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              {/* SolutionsDropdown for mobile will have its own internal padding logic */}
              <SolutionsDropdown mobile /> 
              <Link 
                to="/about" 
                className="block py-2.5 px-3 w-full text-lg text-white hover:text-forti-fi transition-colors rounded-md hover:bg-white/5 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <div className="pt-4 mt-auto w-full"> {/* Pushes button to bottom */}
                <Button 
                  asChild 
                  className="w-full bg-gradient-to-r from-forti-fi to-forti-fi-dark text-white py-3 text-base"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Link to="/contact">
                    Contact
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
